/**
 * What one sale pays, modelled — the arithmetic the creator calculator runs.
 *
 * The `/creators` page recomputes this on every keystroke as somebody drags a
 * price around, and the submission flow shows the same table before a studio
 * commits to a rung. Neither can wait for a round-trip, and both must agree
 * with what the ledger will actually write, which is why this uses the real
 * split engine rather than a percentage.
 *
 * The board itself — which reads `modelledEarningsPerSale` to sort and band —
 * needs the database and stays in the API.
 */

import { computeRevenueSplit, type Currency, type RevenueSplit } from './ledger';

/**
 * Modelling assumptions used for earnings per sale before a title has enough
 * real sales to measure from.
 *
 * Taken from the section 11.1 worked example: a Polish buyer at 23% VAT with
 * payment and processing around 3.5% plus 0.25 EUR. They are stated on the
 * card rather than hidden, because a creator planning a video deserves to know
 * whether a number is measured or modelled.
 */
export const MODELLED_TAX_BPS = 2300;
export const MODELLED_FEE_BPS = 350;
export const MODELLED_FEE_FIXED_MINOR = 25;

/**
 * One sale at a given list price, modelled end to end.
 *
 * Uses the real split engine rather than a percentage, so every figure here is
 * produced by the same code that will later write the ledger entry — and the
 * whole chain is returned rather than one number out of it, because two screens
 * need different parts of it and they must not disagree.
 *
 * The creator board reads the commission: what a creator would earn on one
 * sale. The submission flow reads all of it — what the creator gets, what the
 * studio gets, what GOODBUILD takes and what tax and processing took first.
 * Since #129 the rate is not the studio's to choose, so that table is no longer
 * a decision aid; it is the answer to *what does listing at this price actually
 * pay me*, which is the question the price question raises and the one a studio
 * would otherwise work out on paper after signing.
 *
 * Null rather than zero when the price cannot cover its own costs, so a caller
 * can say so rather than printing a row of nothings. Section 5.4's floor is
 * what stops that reaching checkout; this is the same fact, earlier.
 */
export function modelledSale(
  currency: Currency,
  priceMinor: number,
  creatorRateBps: number,
  platformRateBps: number,
): RevenueSplit | null {
  /**
   * VAT is extracted from the price, not added to it.
   *
   * EU consumer prices are displayed inclusive of VAT, so a 19.99 listing is
   * what the player pays and the tax sits inside it: 19.99 x 23/123 = 3.74,
   * which is the figure in the section 11.1 worked example. Adding 23% on top
   * instead would both overcharge the buyer and understate the creator's
   * commission.
   *
   * **This is why the model is a European buyer rather than an American one.**
   * US sales tax is added at the till and is the buyer's, so it changes what
   * they pay and takes nothing out of the studio's net — a US example would
   * show the same net with a larger number above it, which is the less
   * informative of the two. The prices here are in euros and this codebase
   * converts no currency, so a dollar example would also need a rate somebody
   * invented. The caption names the buyer, and the buyer varies.
   */
  const tax = Math.round((priceMinor * MODELLED_TAX_BPS) / (10_000 + MODELLED_TAX_BPS));
  const fees = Math.round((priceMinor * MODELLED_FEE_BPS) / 10_000) + MODELLED_FEE_FIXED_MINOR;

  // A price too small to cover its own fees cannot pay a commission either.
  if (priceMinor - tax - fees <= 0) return null;

  return computeRevenueSplit({
    currency,
    pricePaid: priceMinor,
    tax,
    paymentFees: fees,
    creatorRateBps,
    platformRateBps,
  });
}

/**
 * Earnings on one sale at a given list price.
 *
 * The commission out of `modelledSale`, and zero where there is no sale to
 * model — the board bands and sorts on this, and a null would have to be
 * special-cased in six places to mean the same thing.
 */
export function modelledEarningsPerSale(
  currency: Currency,
  priceMinor: number,
  creatorRateBps: number,
  platformRateBps: number,
): number {
  return modelledSale(currency, priceMinor, creatorRateBps, platformRateBps)?.commission ?? 0;
}
